import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

// Type for the decoded JWT token
interface DecodedToken {
  exp: number;
}

// Function to determine the base URL for the API based on the current host
const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const tenant = hostname.split('.')[0];

  if (hostname.includes('localhost')) {
    return `http://${tenant}.localhost:8000/api/`; // Local development
  }

  return process.env.REACT_APP_API_BASE_URL || `https://${tenant}.portal-worx.com/api/`;
};


// Create a configured axios instance with default settings
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous token refreshes
let isRefreshingToken = false;
let refreshPromise: Promise<void> | null = null;

// Function to refresh the JWT token if it's close to expiring
const refreshTokenIfNeeded = async (): Promise<void> => {
  if (isRefreshingToken && refreshPromise) {
    await refreshPromise;
    return;
  }

  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token') || '';

  if (token) {
    const decodedToken = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;

    // Check if the token is close to expiring (within 2 minutes)
    if (decodedToken.exp < currentTime + 120) {
      isRefreshingToken = true;

      refreshPromise = axios
        .post(`${getApiBaseUrl()}auth/refresh/`, { refresh: refreshToken })
        .then((response) => {
          const newToken = response.data.access;
          localStorage.setItem('token', newToken ?? '');
        })
        .catch((error) => {
          console.error('Error refreshing token:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        })
        .finally(() => {
          isRefreshingToken = false;
          refreshPromise = null;
        });

      await refreshPromise;
    }
  }
};

// Interceptor to add token and tenant header to requests
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    await refreshTokenIfNeeded();
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Attach tenant header
    config.headers['X-Tenant-Domain'] = window.location.hostname;

    // Handle FormData requests
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor to handle specific response scenarios like timeouts or unauthorized access
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED') {
      console.error('Request timed out:', error);
      alert('Request timed out. Please try again.');
    }
    if (error.response && error.response.status === 401) {
      console.error('Unauthorized error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Activity listener to refresh token during user activity
let activityTimeout: NodeJS.Timeout;

const resetActivityTimeout = () => {
  clearTimeout(activityTimeout);

  // Check and refresh the token after any user activity
  refreshTokenIfNeeded();

  // Set a timeout to log out after inactivity (e.g., 30 minutes)
  activityTimeout = setTimeout(() => {
    console.warn('User has been inactive for too long. Logging out...');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }, 30 * 60 * 1000); // 30 minutes
};

// Attach activity listeners to the window
['click', 'keydown', 'mousemove', 'scroll'].forEach((event) =>
  window.addEventListener(event, resetActivityTimeout)
);

// Initialize the activity timeout on page load
resetActivityTimeout();

export default axiosInstance;
